import clsx from 'clsx';
import { type CSSProperties } from 'react';

import { extract } from './extract-util.js';
import { type GapProps, getGapSprinkles } from './getGapSprinkles.js';
import {
  type GridFlexPositionProps,
  getGridFlexPositionSprinkles,
} from './getGridFlexPositionSprinkles.js';
import {
  type LayoutSizeProps,
  getLayoutSizeStyles,
} from './getLayoutSizeStyles.js';
import {
  type SpacingProps,
  getSpacingSprinkles,
} from './getSpacingSprinkles.js';
import { globalSprinkles } from './safe-sprinkles.js';

/**
 * Defines the grid item props.
 * @public
 */
export interface GridItemStyleProps {
  /**
   * Determines whether this is a grid item.
   * If true, display: 'grid' style will not be added.
   */
  gridItem?: boolean;
}

/**
 * Keys that will define the grid item
 * @public
 */
const gridItemPropKeys: Array<keyof GridItemStyleProps> = ['gridItem'];

/**
 * Defines the grid style props.
 * @public
 */
export interface GridStyleProps
  extends
    GridFlexPositionProps,
    GapProps,
    LayoutSizeProps,
    SpacingProps,
    GridItemStyleProps {
  /* Flex item props */
  /** Defines order */
  order?: CSSProperties['order'];
  /** Defines flex grow */
  flexGrow?: CSSProperties['flexGrow'];
  /** Defines flex shrink */
  flexShrink?: CSSProperties['flexShrink'];
  /** Defines flex basis */
  flexBasis?: CSSProperties['flexBasis'];
  /** Defines flex */
  flex?: CSSProperties['flex'];
  /* Grid container props */
  /** Defines grid template columns */
  gridTemplateColumns?: CSSProperties['gridTemplateColumns'];
  /** Defines grid template rows */
  gridTemplateRows?: CSSProperties['gridTemplateRows'];
  /** Defines grid template areas */
  gridTemplateAreas?: CSSProperties['gridTemplateAreas'];
  /** Defines grid template */
  gridTemplate?: CSSProperties['gridTemplate'];
  /** Defines grid auto columns */
  gridAutoColumns?: CSSProperties['gridAutoColumns'];
  /** Defines grid auto rows */
  gridAutoRows?: CSSProperties['gridAutoRows'];
  /** Defines grid auto flow */
  gridAutoFlow?: CSSProperties['gridAutoFlow'];
  /** Defines grid */
  grid?: CSSProperties['grid'];
  /* Grid item props */
  /** Defines grid column start */
  gridColumnStart?: CSSProperties['gridColumnStart'];
  /** Defines grid column end */
  gridColumnEnd?: CSSProperties['gridColumnEnd'];
  /** Defines grid row start */
  gridRowStart?: CSSProperties['gridRowStart'];
  /** Defines grid row end */
  gridRowEnd?: CSSProperties['gridRowEnd'];
  /** Defines grid column */
  gridColumn?: CSSProperties['gridColumn'];
  /** Defines grid row */
  gridRow?: CSSProperties['gridRow'];
  /** Defines grid area */
  gridArea?: CSSProperties['gridArea'];
}

/**
 * Keys that will define the grid style.
 * @public
 */
const gridStylePropKeys: Array<keyof GridStyleProps> = [
  /* Flex item props */
  'order',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'flex',
  /* Grid container props */
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridTemplateAreas',
  'gridTemplate',
  'gridAutoColumns',
  'gridAutoRows',
  'gridAutoFlow',
  'grid',
  /* Grid item props */
  'gridColumnStart',
  'gridColumnEnd',
  'gridRowStart',
  'gridRowEnd',
  'gridColumn',
  'gridRow',
  'gridArea',
];

// This one is split out of the getGridStyles to ease typing, as the generic
// would be overly complex when using inline.
/**
 * Extracts the gridItem prop from the remaining props.
 * @internal
 */
function extractGridItem<T extends GridItemStyleProps>(
  props: T,
): {
  extracted: Pick<T, keyof GridItemStyleProps>;
  rest: Omit<T, keyof GridItemStyleProps>;
} {
  const { extracted, rest } = extract(props, gridItemPropKeys);
  return { extracted, rest };
}

/**
 * Helper function that will extract all styling relevant props for the Grid styling
 * and will return them as classnames and style attributes.
 * The remaining props returned are the ones that no longer contain any Grid styling
 * related entries.
 * @internal
 */
export function getGridStyles<T extends GridStyleProps>(
  props: T,
  defaults: Partial<GridStyleProps> = {},
): [
  className: string,
  style: CSSProperties,
  remainingProps: Omit<T, keyof GridStyleProps>,
] {
  const { extracted: additionalGridStyles, rest } = extract(
    props,
    gridStylePropKeys,
  );

  const [sizeStyles, afterSizeExtraction] = getLayoutSizeStyles(rest, defaults);

  const [gridFlexClassNames, gridFlexRemainingProps] =
    getGridFlexPositionSprinkles(afterSizeExtraction, defaults);

  const [spacingClassNames, spacingRemainingProps] = getSpacingSprinkles(
    gridFlexRemainingProps,
    defaults,
  );

  const [gapClassNames, gapRemainingProps] = getGapSprinkles(
    spacingRemainingProps,
    defaults,
  );

  const { extracted: gridItemProps, rest: remainingProps } =
    extractGridItem(gapRemainingProps);

  const gridItemClassName = gridItemProps.gridItem
    ? undefined
    : globalSprinkles({ display: 'grid' });

  return [
    clsx(
      gridFlexClassNames,
      spacingClassNames,
      gapClassNames,
      gridItemClassName,
    ),
    {
      ...sizeStyles,
      ...additionalGridStyles,
    },
    remainingProps as Omit<T, keyof GridStyleProps>,
  ];
}
